import { orgAPI } from "@/api/api";
import type {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type {
  SlackMessageEntity,
  SlackMessagesSearchInput,
} from "@cohesive/api-entities";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";

interface UseSlackMessagesSearchProps {
  connection: IntegrationConnection;
  query?: string;
  channel?: string;
  limit?: number;
  sort?: "score" | "timestamp";
  options?: Partial<UseQueryOptions<SlackMessageEntity[], Error>>;
}

export function isSlackMessageEntity(
  entity: any,
): entity is SlackMessageEntity {
  return entity.type === "slack_message";
}

export function useSlackMessagesSearch({
  connection,
  query = "",
  limit = 20,
  sort,
  options = {},
}: UseSlackMessagesSearchProps) {
  return useQuery<SlackMessageEntity[]>({
    queryKey: ["slack-messages-search", connection.id, query, sort, limit],
    queryFn: async () => {
      const input: SlackMessagesSearchInput = {
        maxResults: limit,
        query,
        sort,
      };

      const response = await orgAPI.toolbox.execute<SlackMessageEntity[], any>({
        integration_connection_id: connection.id,
        action_name: "slack-messages-search",
        action_input: input,
      });
      return response.data;
    },
    enabled: !!connection.integration_id,
    staleTime: 1000 * 30, // 30 seconds
    ...options,
  });
}

export function useSlackMessagesSearchInfinite({
  connection,
  query = "",
  limit = 20,
  sort,
  options = {},
}: UseSlackMessagesSearchProps & {
  options?: Partial<
    UseInfiniteQueryOptions<
      ExecuteToolboxResponse<SlackMessageEntity[]>,
      Error,
      InfiniteData<ExecuteToolboxResponse<SlackMessageEntity[]>>
    >
  >;
}) {
  return useInfiniteQuery({
    queryKey: [
      "slack-messages-search-infinite",
      connection.id,
      query,
      sort,
      limit,
    ],
    queryFn: async ({ pageParam = {} }) => {
      const input: SlackMessagesSearchInput = {
        maxResults: limit,
        query,
        sort,
        ...(pageParam as Record<string, any>),
      };

      // Typed messages drive the rich card UI; their generic projections ride
      // along in data_generic and are what get placed on the canvas. Return the
      // full response so the selector can read page.data and page.data_generic.
      return orgAPI.toolbox.execute<SlackMessageEntity[]>({
        integration_connection_id: connection.id,
        action_name: "slack-messages-search",
        action_input: input,
        entity_format: "both",
      });
    },
    getNextPageParam: (lastPage) => {
      // The backend reports pagination via has_more/next; next carries the input
      // overrides (e.g. { page: N }) to spread into the follow-up request.
      if (lastPage.pagination?.has_more) {
        return lastPage.pagination.next;
      }
      return undefined;
    },
    initialPageParam: {},
    enabled: !!connection.integration_id && !!query.trim(),
    staleTime: 1000 * 30, // 30 seconds
    ...options,
  });
}
